import * as Types from '@aeppic/types'

import { EventEmitter } from '@aeppic/shared/event-emitter'

import { FindOptions } from '../model/index.js'
import { Query } from '../model/query/types.js'
import type { Change } from '../model/change-tracker.js'
import type {
  BatchFlushOptions,
  IDocumentLookup,
  IResolvedLockRights,
  IServerAdapter,
} from '../shared/types/server-adapter.js'
import type { LocalModelHistoryOptions, LocalModelJournalEntry, LocalModelMonitor } from './local-model.js'
import { perf } from './perf.js'

export type LocalFindResult = {
  handled: boolean
  documents?: Types.Document[]
  reason?: string
}

export type LocalDocumentsResult = {
  handled: boolean[]
  documents: Array<Types.Document|null>
}

export interface ILocalReadModelAdapter {
  setForegroundActivity?(active: boolean): void
  setSyncIdleMs?(idleMs?: number): void
  find(query: Query, options?: FindOptions): Promise<LocalFindResult>
  getDocuments?(documentLookups: IDocumentLookup[]): Promise<LocalDocumentsResult>
  seedBootDocuments?(documents: Types.Document[], options?: { reset?: boolean }): Promise<any>|any
  /** The seed is complete; `baseHash` is the server's (`Aeppic-Base-Hash` of `_boot`). */
  finishBootSeed?(options?: { baseHash?: string|null }): Promise<any>|any
  startInitialSync?(options?: { onProgress?: (progress: any) => void }): Promise<any>|any
  criticalReady?: Promise<any>
  routingReady?: Promise<any>
  ready?: Promise<any>
  client?: EventTarget & { request?<T = any>(type: string, payload?: Record<string, unknown>): Promise<T> }
  /** This tab's worker role and the running migration, before any event. */
  role?: string
  migration?: any
  /** Change the database owner's reader workers at runtime. */
  setReaders?(config: boolean | { enabled?: boolean, count?: number }): Promise<any>
  /** Stored lifecycle entries across boots. */
  journal?(options?: LocalModelHistoryOptions): Promise<LocalModelJournalEntry[]>
  /** Live lifecycle entries; returns an unsubscribe function. */
  onJournal?(handler: (entry: LocalModelJournalEntry) => void): () => void
  close?(): Promise<void>|void
}

export type LocalReadModelFactory = (context?: { featureOptions?: any }) => ILocalReadModelAdapter|Promise<ILocalReadModelAdapter>

export type LocalFirstServerAdapterOptions = {
  enabled?: () => boolean
  initializationTimeoutMs?: number
  requestTimeoutMs?: number
  /** How long finishBootSeed may wait for the worker before reads stop blocking on it. */
  bootSeedAckMs?: number
  /** How long a design or control lookup waits for local start-up before asking the server. */
  lookupWaitMs?: number
  /** A lookup that waited at least this long for local start-up is logged. */
  slowLookupWaitMs?: number
  /** How long the page waits for the worker's answer before asking the server. */
  lookupAnswerMs?: number
  /** @internal */
  monitor?: LocalModelMonitor
}

export type LocalLookupRequest =
  | { kind: 'design', formId: string, names: string[], strategy: string }
  | { kind: 'controls', formId: string, version: string, strategy: string, variant: Record<string, string> }

export const LOCAL_READ_MODEL_FEATURE = 'local-read-model'
const DEFAULT_LOOKUP_WAIT_MS = 2500
const DEFAULT_LOOKUP_ANSWER_MS = 500
const NO_ANSWER = Symbol('no answer')
const READ_MODEL_LOGGED_PHASES = /receiving|publishing|saving|preparing|authenticat|opening-session|starting|catching|resolving/
export const LOCAL_READ_MODEL_MEMORY_KEY = 'ae-local-read-model'
/** Set by `Aeppic.LocalModel.disable()`: this browser does not start the local model. */
export const LOCAL_READ_MODEL_SWITCHED_OFF_KEY = 'ae-local-read-model-disabled'
const DEFAULT_BOOT_SEED_ACK_MS = 10_000

const FORWARDED_EVENTS = [
  'change',
  'changes:batch',
  'changes:missed',
  'changes:missed-found',
  'client:outdated',
  'connection:parse:error',
  'connection:status:changed',
  'developer:message',
  'server:changed',
  'server:message',
  'upload:progress',
  'uploaded',
]

/**
 * Routes covered reads through a local read model while preserving the remote
 * adapter as the authority for misses, writes, commands, rights, and uploads.
 */
export class LocalFirstServerAdapter extends EventEmitter implements IServerAdapter {
  private _foregroundReads = 0
  private _local: ILocalReadModelAdapter|null = null
  private _localFactory: LocalReadModelFactory|null = null
  private _localInitialization: Promise<ILocalReadModelAdapter|null>|null = null
  private _localInitializationAttempted = false
  private _localDisabled = false
  private _localDisabledError: any = null
  private _localReady = false
  private _localDisabledWaiters = new Set<() => void>()
  private _localMonitorAttached = false
  private _instrumentedLocalClients = new WeakSet<object>()
  private _localFactoryContext: { featureOptions?: any } = {}
  private _localBootSeedStarted = false
  private _pendingBootSeedBatches: Types.Document[][] = []
  // This page's boot seed, kept for a local model started later (retry, enable).
  private _bootSeedHistory: Types.Document[][] = []
  private _bootSeedHistoryFinished = false
  private _bootSeedPending = false
  private _bootSeedFinishRequested = false
  private _bootSeedFinished = false
  private _bootSeedFlush: Promise<void>|null = null
  private _bootSeedWaiters: Array<() => void> = []
  /** The base hash of this page load's `_boot`, kept for retries. */
  private _baseHash: string|null = null
  private _localSynchronizationGeneration = 0
  private _synchronizationState: 'idle' | 'synchronizing' | 'ready' | 'failed' = 'idle'
  private _workerState = 'unknown'
  private _syncWorkActive = false
  private _localRoutingPermitted = false
  private _queryRoutingDegraded = false
  private _disableEpoch = 0
  private _preloadAbandoned = false
  private _preloadNoted = false
  private _featureStopLogged = false
  private _lastShownProgress = ''
  private _lastShownProgressAt = 0

  constructor(
    private _remote: IServerAdapter,
    local: ILocalReadModelAdapter|LocalReadModelFactory,
    private _options: LocalFirstServerAdapterOptions = {},
  ) {
    super()
    if (!_remote || !local) throw new Error('LocalFirstServerAdapter requires a remote adapter and a local adapter or factory')
    this._options.monitor?.markConfigured()
    if (typeof local === 'function') {
      this._localFactory = local
    } else {
      this._local = local
      this._attachLocalMonitor(local)
    }
    this._forwardRemoteEvents()
  }

  /**
   * Opens the local database before the feature flag arrives. Call this only
   * when localStorage remembers that the feature was enabled last time.
   * Synchronization stays deferred until startInitialSync. If the flag is off,
   * call abandonPreloadedLocal().
   */
  preloadLocal(): Promise<ILocalReadModelAdapter|null>|null {
    if (this._local || !this._localFactory) return null
    this._preloadAbandoned = false
    this._notePreload()
    this._options.monitor?.initializing()
    return this._getLocal()
  }

  /**
   * Closes a database opened by preloadLocal when the feature flag is off.
   */
  /**
   * A design or control lookup answered by the local read model, shaped like
   * the `/api/forms/:id/design` and `/controls` responses; `null` means the
   * server answers it (local model off, not loaded yet, or data outside it).
   */
  async lookup(request: LocalLookupRequest): Promise<any | null> {
    if (!this._isEnabled() || this._localDisabled) return null
    const waitStarted = this._now()
    const local: any = await this._localForLookup()
    const waitedMs = Math.round(this._now() - waitStarted)
    if (waitedMs >= (this._options.slowLookupWaitMs ?? 100)) {
      console.info(`[local-model] ${request.kind} lookup for ${request.formId} waited ${waitedMs} ms for the local model to start`)
    }
    if (typeof local?.lookup !== 'function') return null
    const answerMs = this._options.lookupAnswerMs ?? DEFAULT_LOOKUP_ANSWER_MS
    let timeout: ReturnType<typeof setTimeout>|null = null
    try {
      // A worker busy with sync work must not hold up rendering; it still
      // finishes, so the next lookup for this form is answered from its cache.
      const answer = await Promise.race([
        local.lookup(request),
        new Promise(resolve => { timeout = setTimeout(() => resolve(NO_ANSWER), answerMs) }),
      ])
      if (answer !== NO_ANSWER) return answer
      console.warn(`[local-model] ${request.kind} lookup for ${request.formId}: no answer from the local model within ${answerMs} ms; asking the server`)
      return null
    } catch (error) {
      console.warn('[local-model] local lookup failed; asking the server', error)
      return null
    } finally {
      if (timeout) clearTimeout(timeout)
    }
  }

  /**
   * Unlike reads, a lookup waits for local start-up and this page's boot seed
   * (bounded by `lookupWaitMs`): the local answer is what saves the request.
   */
  private async _localForLookup(): Promise<ILocalReadModelAdapter|null> {
    if (!this._local && !this._localInitialization && !this._bootSeedPending) return null
    const ready = (async () => {
      const local = this._local ?? await this._localInitialization
      await this._bootSeedSettled()
      return local && this._local === local && !this._localDisabled ? local : null
    })()
    let timeout: ReturnType<typeof setTimeout>|null = null
    let disabledWaiter!: () => void
    const disabled = new Promise<null>(resolve => {
      disabledWaiter = () => resolve(null)
      this._localDisabledWaiters.add(disabledWaiter)
    })
    try {
      return await Promise.race([
        ready,
        disabled,
        new Promise<null>(resolve => {
          timeout = setTimeout(() => resolve(null), this._options.lookupWaitMs ?? DEFAULT_LOOKUP_WAIT_MS)
        }),
      ])
    } finally {
      this._localDisabledWaiters.delete(disabledWaiter)
      if (timeout) clearTimeout(timeout)
    }
  }

  private _bootSeedSettled(): Promise<void> {
    if (!this._bootSeedPending) return Promise.resolve()
    return new Promise(resolve => this._bootSeedWaiters.push(resolve))
  }

  private _settleBootSeed(): void {
    this._bootSeedPending = false
    for (const resolve of this._bootSeedWaiters.splice(0)) resolve()
  }

  abandonPreloadedLocal(): void {
    this._preloadAbandoned = true
    const current = this._local
    this._local = null
    this._localReady = false
    current?.close?.()
  }

  /**
   * Revokes local answers immediately, tells every connected tab to reload,
   * then closes SQLite and deletes the OPFS database.
   */
  async disableLocalModel(): Promise<void> {
    this._logFeatureStopped()
    this._localReady = false
    this._localRoutingPermitted = false
    this._localDisabled = true
    if (!this._local && this._localInitialization) {
      await this._localInitialization.catch(() => null)
    }
    const local = this._local
    const client = local?.client
    if (client && typeof client.request === 'function') {
      await client.request('disableLocalModel').catch(() => {})
    } else {
      local?.close?.()
    }
  }

  /**
   * Stop using the local model in this tab: reads and lookups go to the
   * server from now on. Unlike disableLocalModel, the local data is kept.
   */
  async closeLocal(): Promise<void> {
    this._localReady = false
    this._localRoutingPermitted = false
    this._localDisabled = true
    this._releaseLocalDisabledWaiters()
    this._settleBootSeed()
    if (!this._local && this._localInitialization) {
      await this._localInitialization.catch(() => null)
    }
    const local = this._local
    this._local = null
    // A later retryLocal starts it again; nothing else does.
    this._localInitializationAttempted = true
    this._options.monitor?.featureDisabled()
    await local?.close?.()
  }

  /**
   * Initializes the local adapter only after Aeppic has loaded and enabled the
   * corresponding feature. Failures are reported and leave remote reads intact.
   */
  async initializeLocal(featureOptions?: any): Promise<ILocalReadModelAdapter|null> {
    if (!this._isEnabled()) {
      this._options.monitor?.featureDisabled()
      this._logFeatureStopped()
      if (this._local || this._localInitialization) this.abandonPreloadedLocal()
      return null
    }
    this._preloadAbandoned = false
    this._localFactoryContext = { featureOptions }
    if (featureOptions?.syncIdleMs != null) this._local?.setSyncIdleMs?.(featureOptions.syncIdleMs)
    this._options.monitor?.initializing()
    return this._getLocal()
  }

  async retryLocal(featureOptions?: any): Promise<ILocalReadModelAdapter|null> {
    if (!this._isEnabled()) {
      this._options.monitor?.featureDisabled()
      return null
    }
    if (!this._localFactory) throw new Error('Local read model retry requires a factory')
    if (this._localInitialization) return this._localInitialization

    const previousLocal = this._local
    this._local = null
    this._localReady = false
    this._localMonitorAttached = false
    this._localInitializationAttempted = false
    this._localDisabled = false
    this._localDisabledError = null
    this._queryRoutingDegraded = false
    this._syncWorkActive = false
    this._localRoutingPermitted = false
    this._localBootSeedStarted = false
    this._bootSeedFinished = false
    // The new worker gets this page's boot seed again.
    this._pendingBootSeedBatches = this._bootSeedHistory.slice()
    this._bootSeedFinishRequested = this._bootSeedHistoryFinished
    if (this._pendingBootSeedBatches.length) this._bootSeedPending = true
    this._localSynchronizationGeneration += 1
    this._localFactoryContext = { featureOptions }
    this._options.monitor?.initializing()
    await previousLocal?.close?.()
    return this._getLocal()
  }

  /** Keep reads remote until this startup's boot seed has been acknowledged. */
  beginBootSeed(): void {
    this._bootSeedPending = true
  }

  /**
   * Queue a snapshot; importing into the v3 model must not await the worker.
   * The page's seed is remembered, so a local model started later in this
   * page (retry, enable) receives it too.
   */
  async seedBootDocuments(documents: Types.Document[]): Promise<void> {
    if (!documents.length) return
    this._bootSeedHistory.push(JSON.parse(JSON.stringify(documents)))
    if (!this._isEnabled()) return
    this._bootSeedPending = true
    this._pendingBootSeedBatches.push(JSON.parse(JSON.stringify(documents)))
    void this._getLocal().then(local => local && this._flushBootSeed(local))
  }

  /**
   * Queue the end marker behind all batches, without blocking the caller.
   * `baseHash`: the server's (`Aeppic-Base-Hash` of `_boot`); a retry
   * without one keeps this page load's.
   */
  async finishBootSeed(options?: { baseHash?: string|null }): Promise<void> {
    if (options?.baseHash !== undefined) this._baseHash = options.baseHash
    this._bootSeedHistoryFinished = true
    if (!this._isEnabled()) return
    this._bootSeedFinishRequested = true
    void this._getLocal().then(local => local && this._flushBootSeed(local))
  }

  async startInitialSync(startupWaitMs = 2500, options?: { blockUntilCriticalReady?: boolean }): Promise<{ timedOut: boolean }> {
    if (!this._isEnabled()) return { timedOut: false }
    const criticalReady = this._startInitialSyncWhenReady()
    if (options?.blockUntilCriticalReady) {
      await criticalReady.catch(() => {})
      return { timedOut: false }
    }
    const waitMs = Math.max(0, Number(startupWaitMs) || 0)
    if (waitMs === 0) {
      void criticalReady.catch(() => {})
      return { timedOut: true }
    }
    let timeout: ReturnType<typeof setTimeout>|null = null
    const timedOut = await Promise.race([
      criticalReady.then(() => false, () => false),
      new Promise<boolean>(resolve => {
        timeout = setTimeout(() => resolve(true), waitMs)
      }),
    ])
    if (timeout) clearTimeout(timeout)
    return { timedOut }
  }

  private async _startInitialSyncWhenReady(): Promise<any> {
    const local = await this._getLocal()
    if (!local?.startInitialSync) return null
    await this._flushBootSeed(local)
    if (this._local !== local || this._localDisabled) return null
    const synchronization = perf.track('local-read:initial-sync', () => Promise.resolve(local.startInitialSync()), 'startup')
    synchronization.catch(error => {
      console.error('[local-model] initial synchronization failed; remote reads remain authoritative', error)
    })
    const cached = (<any>local).initStats
    if (cached?.storage === 'OPFS' && cached.documentCount > 0) return local.routingReady ?? synchronization
    return local.criticalReady ?? local.routingReady ?? synchronization
  }

  private _flushBootSeed(local: ILocalReadModelAdapter): Promise<void> {
    if (this._bootSeedFlush) return this._bootSeedFlush
    this._bootSeedFlush = this._runBootSeedFlush(local).then(() => {
      this._bootSeedFlush = null
      // A producer can enqueue a batch/end marker as the previous flush ends.
      if (this._local === local && !this._localDisabled &&
          (this._pendingBootSeedBatches.length || (this._bootSeedFinishRequested && !this._bootSeedFinished))) {
        return this._flushBootSeed(local)
      }
    })
    return this._bootSeedFlush
  }

  private async _runBootSeedFlush(local: ILocalReadModelAdapter): Promise<void> {
    try {
      while (this._local === local && this._pendingBootSeedBatches.length) {
        const documents = this._pendingBootSeedBatches[0]
        if (local.seedBootDocuments) {
          await this._acknowledgeBootSeed(
            local.seedBootDocuments(documents, { reset: !this._localBootSeedStarted }),
            'seedBootDocuments',
          )
          this._localBootSeedStarted = true
        }
        this._pendingBootSeedBatches.shift()
      }
      if (this._local === local && this._bootSeedFinishRequested && !this._bootSeedFinished) {
        await this._acknowledgeBootSeed(local.finishBootSeed?.({ baseHash: this._baseHash }), 'finishBootSeed')
        this._bootSeedFinished = true
        this._settleBootSeed()
      }
    } catch (error) {
      if (error?.code === 'LOCAL_MODEL_BOOT_SEED_TIMEOUT') {
        console.warn('[local-model] boot seed was not acknowledged; later reads are no longer held for it')
        // Drop the unacknowledged batches: keeping them queued makes every flush
        // resend them to the stalled worker forever. Initial sync supplies the data.
        this._pendingBootSeedBatches = []
        this._settleBootSeed()
        this._bootSeedFinished = true
        return
      }
      this._disableLocal(local, error, 'LOCAL_MODEL_BOOT_SEED_FAILED')
    }
  }

  private _acknowledgeBootSeed<T>(work: Promise<T>|T|undefined, label: string): Promise<T|undefined> {
    const timeoutMs = this._options.bootSeedAckMs ?? DEFAULT_BOOT_SEED_ACK_MS
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        const error = new Error(`${label} was not acknowledged`)
        ;(error as { code?: string }).code = 'LOCAL_MODEL_BOOT_SEED_TIMEOUT'
        reject(error)
      }, timeoutMs)
      Promise.resolve(work).then(
        value => {
          clearTimeout(timer)
          resolve(value)
        },
        error => {
          clearTimeout(timer)
          reject(error)
        },
      )
    })
  }

  get isOnline() {
    return (<any>this._remote).isOnline
  }

  connect() {
    return this._remote.connect()
  }

  disconnect() {
    return this._remote.disconnect()
  }

  setClientId(id: string) {
    return this._remote.setClientId(id)
  }

  sendToWebsocket(message: object) {
    return this._remote.sendToWebsocket(message)
  }

  connectDeveloper(sessionId: string) {
    return this._remote.connectDeveloper(sessionId)
  }

  disconnectDeveloper() {
    return this._remote.disconnectDeveloper()
  }

  sendToDeveloperWebsocket(message: object) {
    return this._remote.sendToDeveloperWebsocket(message)
  }

  setCookies(cookieString: string) {
    // The local model holds public data only: logins do not touch it.
    this._remote.setCookies(cookieString)
  }

  getFormVersions(formId: Types.IdOrReference) {
    return this._foregroundRead(() => this._remote.getFormVersions(formId))
  }

  private _now(): number {
    return typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now()
  }

  private _trackRead<T>(operation: 'find' | 'get', label: string, started: number, route: 'local' | 'remote' | 'mixed', reason: string | undefined, work: Promise<T>): Promise<T> {
    return work.then(result => {
      this._options.monitor?.recordRoute(route, reason, {
        ms: Math.max(0, this._now() - started),
        operation,
        label,
      })
      return result
    }, error => {
      this._options.monitor?.recordRoute(route, reason, {
        ms: Math.max(0, this._now() - started),
        operation,
        label,
      })
      throw error
    })
  }

  private _foregroundRead<T>(read: () => Promise<T>): Promise<T> {
    this._foregroundReads++
    if (this._foregroundReads === 1) this._local?.setForegroundActivity?.(true)
    const finish = () => {
      this._foregroundReads--
      if (this._foregroundReads === 0) this._local?.setForegroundActivity?.(false)
    }
    try { return Promise.resolve(read()).finally(finish) }
    catch (error) { finish(); return Promise.reject(error) }
  }

  async getDocuments(documentLookups: IDocumentLookup[]): Promise<Types.Document[]> {
    const started = this._now()
    const label = documentLookups.slice(0, 4).map(lookup => lookup?.id).filter(Boolean).join(',')
    if (this._bootSeedPending) {
      return this._trackRead('get', label, started, 'remote', 'boot-seed-pending', this._foregroundRead(() => this._remote.getDocuments(documentLookups)))
    }
    if (!this._isEnabled()) {
      return this._trackRead('get', label, started, 'remote', 'feature-disabled', this._foregroundRead(() => this._remote.getDocuments(documentLookups)))
    }
    const localAdapter = await this._getLocal()
    if (!localAdapter?.getDocuments) {
      return this._trackRead('get', label, started, 'remote', 'document-lookup-unsupported', this._foregroundRead(() => this._remote.getDocuments(documentLookups)))
    }
    try {
      const local = await this._whileLocalAvailable(localAdapter.getDocuments(documentLookups))
      if (!Array.isArray(local?.handled) || !Array.isArray(local?.documents)
        || local.handled.length !== documentLookups.length || local.documents.length !== documentLookups.length) {
        return this._trackRead('get', label, started, 'remote', 'document-lookup-malformed', this._foregroundRead(() => this._remote.getDocuments(documentLookups)))
      }
      const missingIndexes = local.handled
        .map((handled, index) => handled && local.documents[index] ? -1 : index)
        .filter(index => index >= 0)
      if (!missingIndexes.length) {
        return this._trackRead('get', label, started, 'local', undefined, Promise.resolve(local.documents))
      }
      const remoteLookups = missingIndexes.map(index => documentLookups[index])
      const remoteDocuments = await this._foregroundRead(() => this._remote.getDocuments(remoteLookups))
      const remoteByLookup = new Map(remoteDocuments.filter(Boolean).map(document => [`${document.id}@${document.v}`, document]))
      for (const index of missingIndexes) {
        const lookup = documentLookups[index]
        local.documents[index] = remoteByLookup.get(`${lookup.id}@${lookup.v}`)
          ?? remoteDocuments.find(document => document?.id === lookup.id)
          ?? null
      }
      return this._trackRead('get', label, started, 'mixed', 'document-miss', Promise.resolve(local.documents))
    } catch (error) {
      const reason = error?.code === 'LOCAL_MODEL_NOT_READY' ? 'local-not-ready' : 'local-error'
      return this._trackRead('get', label, started, 'remote', reason, this._foregroundRead(() => this._remote.getDocuments(documentLookups)))
    }
  }

  find(query: Query, options?: FindOptions): Promise<Types.Document[]> {
    const started = this._now()
    if (this._bootSeedPending) {
      return this._trackRead('find', queryLabel(query), started, 'remote', 'boot-seed-pending', this._foregroundRead(() => this._remote.find(query, options)))
    }
    if (!this._isEnabled()) {
      return this._trackRead('find', queryLabel(query), started, 'remote', 'feature-disabled', this._foregroundRead(() => this._remote.find(query, options)))
    }
    // Already connected: start the local read in this turn so a burst of finds
    // is queued before the worker or the server batch flushes.
    if (this._local) return this._finishLocalFind(this._local, query, options, started)
    return this._findAfterLocalConnect(query, options, started)
  }

  private async _findAfterLocalConnect(query: Query, options: FindOptions | undefined, started: number): Promise<Types.Document[]> {
    const localAdapter = await this._getLocal()
    if (!localAdapter) {
      return this._trackRead('find', queryLabel(query), started, 'remote', 'local-unavailable', this._foregroundRead(() => this._remote.find(query, options)))
    }
    return this._finishLocalFind(localAdapter, query, options, started)
  }

  private _finishLocalFind(localAdapter: ILocalReadModelAdapter, query: Query, options: FindOptions | undefined, started: number): Promise<Types.Document[]> {
    const label = queryLabel(query)
    let work: Promise<LocalFindResult>
    try {
      work = this._whileLocalAvailable(Promise.resolve(localAdapter.find(query, options)))
    } catch (error) {
      work = Promise.reject(error)
    }
    // The worker transport coalesces messages and resolves each keyed decision.
    // Local hits never wait for remote requests or other queries in that message.
    return work.then(local => {
      if (local?.handled) {
        return this._trackRead('find', label, started, 'local', undefined, Promise.resolve(local.documents ?? []))
      }
      const reason = local?.reason ?? 'uncovered'
      return this._trackRead('find', label, started, 'remote', reason, this._foregroundRead(() => this._remote.find(query, options)))
    }, error => {
      const reason = error?.code === 'LOCAL_MODEL_NOT_READY' ? 'local-not-ready' : 'local-error'
      return this._trackRead('find', label, started, 'remote', reason, this._foregroundRead(() => this._remote.find(query, options)))
    })
  }

  hasRight(identifier: Types.IdOrReference, optionalRightIdentifiers?: Types.IdOrReference[]) {
    return this._remote.hasRight(identifier, optionalRightIdentifiers)
  }

  resolveLockRights(lockIdentifiers: Types.IdOrReference[], options?: { causalDocumentId?: string|null }) {
    return this._remote.resolveLockRights(lockIdentifiers, options)
  }

  writeChanges(changes: Change[]) {
    return this._remote.writeChanges(changes)
  }

  flush() {
    return this._remote.flush()
  }

  pause() {
    return this._remote.pause()
  }

  resume(options?: BatchFlushOptions) {
    return this._remote.resume(options)
  }

  upload(dataUrl: string, content?: ArrayBuffer|File|Blob|string|Buffer, channel?: string) {
    return this._remote.upload(dataUrl, content, channel)
  }

  execute(...args: any[]) {
    const execute = (<any>this._remote).execute
    if (!execute) throw new Error('Remote server adapter does not support command execution')
    return execute.apply(this._remote, args)
  }

  private _forwardRemoteEvents() {
    for (const eventName of FORWARDED_EVENTS) {
      // Server changes do not refresh the local model: its copy is decided
      // at page load (boot seed DTS) and stays for the page's lifetime.
      this._remote.on(eventName, (...args) => this.emit(eventName, ...args))
    }
  }

  /**
   * Stops waiting for a local read when the model is closed. It does not time
   * the read out: a ready SQLite query returns, and an unaccepted one rejects
   * as not ready before this wait matters.
   */
  private _whileLocalAvailable<T>(operation: Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      if (this._localDisabled) {
        reject(this._localDisabledError ?? new Error('Local read model is unavailable'))
        return
      }
      const disabledWaiter = () => {
        reject(this._localDisabledError ?? new Error('Local read model is unavailable'))
      }
      this._localDisabledWaiters.add(disabledWaiter)
      operation.then(result => {
        this._localDisabledWaiters.delete(disabledWaiter)
        resolve(result)
      }, error => {
        this._localDisabledWaiters.delete(disabledWaiter)
        reject(error)
      })
    })
  }

  private _releaseLocalDisabledWaiters() {
    const waiters = [...this._localDisabledWaiters]
    this._localDisabledWaiters.clear()
    for (const waiter of waiters) waiter()
  }

  private _restoreLocalRouting() {
    if (this._localDisabled || !this._localRoutingPermitted || this._syncWorkActive) return
    if (this._queryRoutingDegraded) return
    this._localReady = true
    this._synchronizationState = 'ready'
  }

  private _disableLocal(localAdapter: ILocalReadModelAdapter, error: any, code: string) {
    if (this._local !== localAdapter || this._localDisabled) return
    this._local = null
    this._localReady = false
    this._localDisabled = true
    this._localDisabledError = error
    this._localInitializationAttempted = true
    this._releaseLocalDisabledWaiters()
    ;(<any>localAdapter)?.close?.()
    this._options.monitor?.markFailed(error, { code, stage: 'query' })
    this.emit('local:error', error)
  }

  private _getLocal(): Promise<ILocalReadModelAdapter|null> {
    if (this._local) {
      this._attachLocalMonitor(this._local)
      return Promise.resolve(this._local)
    }
    if (this._localInitialization) return this._localInitialization
    if (!this._localFactory || this._localInitializationAttempted) return Promise.resolve(null)

    this._localInitializationAttempted = true
    const localFactory = this._localFactory
    const timeoutMs = Math.max(0, this._options.initializationTimeoutMs ?? 0)
    let timeout: ReturnType<typeof setTimeout>|null = null
    let timedOut = false
    const factoryResult = Promise.resolve().then(() => localFactory(this._localFactoryContext))
    const factoryOrTimeout = timeoutMs > 0
      ? Promise.race([
        factoryResult,
        new Promise<ILocalReadModelAdapter>((_resolve, reject) => {
          timeout = setTimeout(() => {
            timedOut = true
            const error: any = new Error(`Local read model initialization timed out after ${timeoutMs} ms`)
            error.code = 'LOCAL_MODEL_INITIALIZATION_TIMEOUT'
            reject(error)
          }, timeoutMs)
        }),
      ])
      : factoryResult

    void factoryResult.then(local => {
      if (timedOut) (<any>local)?.close?.()
    }).catch(() => {})

    this._localInitialization = factoryOrTimeout
      .then(local => {
        if (timeout) clearTimeout(timeout)
        if (!local) throw new Error('Local read model factory did not return an adapter')
        if (this._preloadAbandoned) {
          this._localInitialization = null
          local.close?.()
          return null
        }
        this._local = local
        this._attachLocalMonitor(local)
        this._localInitialization = null
        return local
      })
      .catch(error => {
        if (timeout) clearTimeout(timeout)
        this._localInitialization = null
        // Reads no longer wait for a seed that cannot be delivered. The queued
        // batches stay for a retry of the local model.
        this._settleBootSeed()
        this._options.monitor?.markFailed(error, {
          code: 'LOCAL_MODEL_INITIALIZATION_FAILED',
          stage: 'initialization',
        })
        this.emit('local:error', error)
        return null
      })

    return this._localInitialization
  }

  private _isEnabled() {
    return this._options.enabled?.() ?? true
  }

  private _attachLocalMonitor(local: ILocalReadModelAdapter) {
    if (this._localMonitorAttached) return
    this._localMonitorAttached = true
    const monitor = this._options.monitor
    const synchronizationGeneration = this._localSynchronizationGeneration
    monitor?.adapterAvailable()

    const client = local.client
    if (this._localFactoryContext.featureOptions?.syncIdleMs != null) {
      local.setSyncIdleMs?.(this._localFactoryContext.featureOptions.syncIdleMs)
    }
    local.setForegroundActivity?.(this._foregroundReads > 0)
    this._instrumentLocalClient(client)
    client?.addEventListener?.('query-performance', (event: CustomEvent) => {
      const timing = event.detail
      if (timing) perf.recordQuery(timing.name, timing.durationMs, timing.status)
    })
    client?.addEventListener?.('local-model-disabled', (event: CustomEvent) => {
      const epoch = Number(event.detail?.epoch)
      if (!Number.isSafeInteger(epoch) || epoch <= this._disableEpoch) return
      this._disableEpoch = epoch
      this._localReady = false
      this._localRoutingPermitted = false
      this._localDisabled = true
      this.emit('local-model-disabled', event.detail ?? { epoch })
    })
    // A newer worker build took over the database; this tab runs the old code:
    // reads fall back to the server until the tab reloads.
    const announceOutdated = (event: CustomEvent) => {
      const required = event.detail?.required ?? {}
      this.emit('client:outdated', {
        reason: 'local-model',
        target: `local-model:${required.schema ?? ''}.${required.revision ?? ''}.${required.build ?? ''}`,
      })
    }
    client?.addEventListener?.('upgrade-required', announceOutdated)
    client?.addEventListener?.('unavailable', (event: CustomEvent) => {
      const detail = event.detail ?? {}
      const error = Object.assign(new Error(detail.message || 'Local model is unavailable'), detail)
      this._disableLocal(local, error, detail.code || 'LOCAL_MODEL_UNAVAILABLE')
    })
    client?.addEventListener?.('status', (event: CustomEvent) => {
      const detail = event.detail ?? {}
      if (typeof detail.phase === 'string') this._workerState = detail.phase
      if (detail.phase === 'syncing') this._synchronizationState = 'synchronizing'
      if (monitor && this._local === local && !this._localDisabled) monitor.reportStatus(detail)
    })
    client?.addEventListener?.('profile-progress', (event: CustomEvent) => {
      const detail = event.detail ?? {}
      this._showSyncProgress(detail)
      if (monitor && this._local === local && !this._localDisabled) monitor.reportProgress(detail)
    })
    client?.addEventListener?.('role', (event: CustomEvent) => {
      if (monitor && this._local === local && event.detail?.role) monitor.reportRole(event.detail.role)
    })
    client?.addEventListener?.('migration', (event: CustomEvent) => {
      if (monitor && this._local === local) monitor.reportMigration(event.detail?.active ?? null)
    })
    if (monitor && typeof local.setReaders === 'function') {
      monitor.setReadersHandler(async config => {
        const status = await local.setReaders!(config)
        const stats = await client?.request?.('stats').catch(() => undefined)
        if (stats && this._local === local) monitor.reportStats(stats)
        return status
      })
    }
    if (monitor && typeof local.role === 'string') monitor.reportRole(local.role as any)
    if (monitor && local.migration) monitor.reportMigration(local.migration)
    if (monitor && typeof local.journal === 'function' && typeof local.onJournal === 'function') {
      monitor.attachJournal({
        read: options => local.journal!(options),
        subscribe: handler => local.onJournal!(handler),
      })
    }
    client?.addEventListener?.('sync-work', (event: CustomEvent) => {
      const active = Boolean(event.detail?.active)
      this._syncWorkActive = active
      if (active) {
        this._synchronizationState = 'synchronizing'
        this._workerState = 'owner'
        this._localReady = false
        return
      }
      if (event.detail?.succeeded === true) {
        this._queryRoutingDegraded = false
        this._restoreLocalRouting()
        return
      }
      this._localReady = false
      this._localRoutingPermitted = false
    })
    if (monitor && typeof client?.request === 'function') {
      void client.request('stats').then(stats => monitor.reportStats(stats ?? {})).catch(() => {})
    }
    // Cached OPFS data may be usable for startup rendering, but it must not
    // receive application queries while the authoritative background sync is
    // still running. The local worker is shared with synchronization, so use
    // the full ready promise as the routing gate.
    const routingReady = local.ready ?? local.routingReady
    const observedGeneration = this._localSynchronizationGeneration
    if (routingReady && typeof routingReady.then === 'function') {
      this._localReady = false
      this._synchronizationState = 'synchronizing'
      void routingReady.then(() => {
        if (this._local !== local || this._localDisabled) return
        if (observedGeneration !== this._localSynchronizationGeneration) return
        this._localRoutingPermitted = true
        this._restoreLocalRouting()
        monitor?.markReady((<any>local).initStats ?? undefined)
      }).catch(error => {
        if (this._local !== local || this._localDisabled) return
        if (observedGeneration !== this._localSynchronizationGeneration) return
        this._localReady = false
        this._synchronizationState = 'failed'
        console.error('[local-model] synchronization failed; remote reads remain authoritative', {
          code: error?.code ?? 'LOCAL_MODEL_SYNCHRONIZATION_FAILED',
          message: error?.message ?? String(error),
        })
        monitor?.markDegraded(error, {
          code: 'LOCAL_MODEL_SYNCHRONIZATION_FAILED',
          stage: 'synchronization',
        })
      })
    } else {
      this._localRoutingPermitted = true
      this._localReady = !this._syncWorkActive
      this._synchronizationState = this._localReady ? 'ready' : 'synchronizing'
    }

    if (local.ready && typeof local.ready.then === 'function') {
      monitor?.syncing()
      void local.ready.then(async result => {
        if (synchronizationGeneration !== this._localSynchronizationGeneration
          || this._local !== local
          || this._localDisabled) return
        let stats = result
        if (monitor && typeof client?.request === 'function') {
          stats = await client.request('stats').catch(() => result)
        }
        if (synchronizationGeneration !== this._localSynchronizationGeneration
          || this._local !== local
          || this._localDisabled) return
        monitor?.markReady(stats ?? undefined)
      }).catch(error => {
        if (synchronizationGeneration !== this._localSynchronizationGeneration
          || this._local !== local
          || this._localDisabled) return
        monitor?.markDegraded(error, {
          code: 'LOCAL_MODEL_SYNCHRONIZATION_FAILED',
          stage: 'synchronization',
        })
      })
    } else {
      monitor?.markReady()
    }
  }

  private _showSyncProgress(detail: Record<string, any>) {
    // @aeppic/read-model-browser logs these progress phases itself; only
    // outcomes (current, updated, skipped, failed) are reported here.
    if (READ_MODEL_LOGGED_PHASES.test(String(detail.phase ?? ''))) return
    const profile = String(detail.profileId ?? 'sync')
    const text = typeof detail.message === 'string' && detail.message
      ? detail.message
      : String(detail.phase ?? '')
    if (!text) return
    const key = `${profile}:${detail.phase ?? ''}:${detail.scopeId ?? ''}:${text}`
    const now = Date.now()
    if (key === this._lastShownProgress && now - this._lastShownProgressAt < 8000) return
    this._lastShownProgress = key
    this._lastShownProgressAt = now
    console.info(`[local-model] ${profile}: ${text}.`.replace(/\.\.$/, '.'))
  }

  private _notePreload() {
    if (this._preloadNoted) return
    this._preloadNoted = true
    console.info('[local-model] starting because it was enabled last time, before features are known')
  }

  private _logFeatureStopped() {
    if (this._featureStopLogged || (!this._preloadNoted && !this._local && !this._localInitialization)) return
    this._featureStopLogged = true
    console.info('[local-model] features are off, stopping the local model')
  }

  private _instrumentLocalClient(client: ILocalReadModelAdapter['client']) {
    if (!client || typeof client.request !== 'function' || this._instrumentedLocalClients.has(client)) return
    const request = client.request.bind(client)
    const instrumented = (type: string, payload?: Record<string, unknown>) => {
      const detail = `type=${type}`
      const spanName = type === 'syncProfile'
        ? 'local-read:syncProfile'
        : type === 'routeQuery'
          ? 'local-read:routeQuery'
          : type === 'getDocuments'
            ? 'local-read:getDocuments'
            : `local-read:rpc:${type}`
      return perf.track(spanName, () => request(type, payload), detail)
    }
    try {
      client.request = instrumented
      this._instrumentedLocalClients.add(client)
    } catch {
      // Some adapters expose a read-only request method; their outer operation
      // spans still provide timing and timeout diagnostics.
    }
  }
}

function queryLabel(query: Query): string {
  const fields: string[] = []
  const visit = (node: any) => {
    if (!node || typeof node !== 'object' || fields.length >= 6) return
    if (typeof node.field === 'string') fields.push(node.field)
    if (Array.isArray(node.conditions)) {
      for (const child of node.conditions) visit(child)
    }
  }
  visit(query?.graph)
  return [...new Set(fields)].join('+') || 'query'
}
